import React from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useMutation, useQuery} from "react-query";
import ngage from "services/ngage";
import {useNotifications} from "context/NotificationContext";
import {invalidate} from "services/query-client";
import Page from "components/Page";
import {MembershipData} from "types/Membership";
import MembershipForm from "pages/Membership/Form";
import {FormikHelpers} from "formik";
import {useOperator} from "../../../context/OperatorContext";

function MutateMembership() {
    const {termFor} = useOperator();
    const {id} = useParams()
    const {data: membership, isLoading} = useQuery(
        ['memberships', {id}],
        () => ngage.memberships.get(parseInt(id as string)),
        {enabled: !!id}
    )

    const store = useMutation((data: MembershipData) => ngage.memberships.store(data))
    const update = useMutation((data: MembershipData) => ngage.memberships.update(parseInt(id as string), data))

    const {notify} = useNotifications()
    const navigate = useNavigate()

    // Handle form submission
    async function handleSubmit(values: MembershipData, {setSubmitting}: FormikHelpers<MembershipData>) {
        id? await handleUpdate(values) : await handleStore(values)
        await invalidate('memberships.all')

        setSubmitting(false)
        navigate('/memberships')
    }

    // Handle store mutation
    async function handleStore(values: MembershipData) {
        try {
            await store.mutateAsync(values)
            notify("success", `${termFor('Membership')} Created`, `A new ${termFor('membership')} has been created`)
        } catch (error) {
            notify("error", `${termFor('Membership Plan')} Not Created`, `A new ${termFor('membership')} could not be stored`)
            throw error
        }
    }

    // Handle update mutation
    async function handleUpdate(values: MembershipData) {
        try {
            await update.mutateAsync(values)
            notify("success", `${termFor('Membership')} Updated`, `The chosen ${termFor('membership')} has been updated`)
            await invalidate(['memberships', {id}])
        } catch (error) {
            notify("error", `${termFor('Membership')} Not Updated`, `The chosen ${termFor('membership')} could not be updated`)
            throw error
        }
    }

    // Prepare breadcrumbs
    const newBreadcrumbs = {'Commercial': '/', [termFor('Memberships')]: '/memberships', [`New ${termFor('Membership')}`]: '/memberships/new'}
    const editBreadcrumbs = {'Commercial': '/', [termFor('Memberships')]: '/memberships', [`Edit ${termFor('Membership')}`]: `/memberships/${id}/edit`}

    return (
        <Page title={id ? `Edit ${termFor('Membership')}` : `New ${termFor('Membership')}`}
              breadcrumbs={id ? editBreadcrumbs : newBreadcrumbs}
              isLoading={isLoading}>
            <MembershipForm membership={membership} onSubmit={handleSubmit}/>
        </Page>
    );
}

export default MutateMembership;